import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FileText, Loader2, MessageSquare, Package, Save, Trash2, X } from "lucide-react";
import {
  API,
  auth,
  COMM_TYPES,
  fmtMoney,
  inputCls,
  labelCls,
  LEAD_SOURCES,
  LEAD_STATUSES,
  ORDER_STATUSES,
  PRODUCT_INTERESTS,
  QUOTE_STATUSES,
  statusMeta,
} from "./crm";

const EMPTY_QUOTE = {
  product: "Vanilla Planifolia Grade A",
  quantity: "",
  unit_price: "",
  shipping_cost: "0",
  discount: "0",
  currency: "USD",
  destination: "",
  payment_terms: "T/T",
};

export const LeadDetail = ({ leadId, token, onClose, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [comm, setComm] = useState({ type: "note", content: "" });
  const [quote, setQuote] = useState(EMPTY_QUOTE);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/leads/${leadId}`, auth(token));
    setDetail(data);
    setForm({ ...data.lead, follow_up_date: data.lead.follow_up_date || "" });
  }, [leadId, token]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setQ = (k) => (e) => setQuote((f) => ({ ...f, [k]: e.target.value }));

  const quoteTotal = useMemo(() => {
    const n = (v) => parseFloat(v) || 0;
    return n(quote.quantity) * n(quote.unit_price) + n(quote.shipping_cost) - n(quote.discount);
  }, [quote]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/admin/leads/${leadId}`,
        { ...form, follow_up_date: form.follow_up_date || null },
        auth(token)
      );
      onChanged();
      load();
    } finally {
      setSaving(false);
    }
  };

  const addComm = async (e) => {
    e.preventDefault();
    if (!comm.content.trim()) return;
    await axios.post(`${API}/admin/leads/${leadId}/communications`, comm, auth(token));
    setComm({ type: "note", content: "" });
    load();
  };

  const createQuote = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/leads/${leadId}/quotations`,
        {
          ...quote,
          quantity: parseFloat(quote.quantity),
          unit_price: parseFloat(quote.unit_price),
          shipping_cost: parseFloat(quote.shipping_cost) || 0,
          discount: parseFloat(quote.discount) || 0,
        },
        auth(token)
      );
      setQuote(EMPTY_QUOTE);
      onChanged();
      load();
    } finally {
      setBusy(false);
    }
  };

  const setQuoteStatus = async (qid, status) => {
    await axios.patch(`${API}/admin/quotations/${qid}`, { status }, auth(token));
    onChanged();
    load();
  };

  const convert = async (qid) => {
    await axios.post(`${API}/admin/quotations/${qid}/convert`, {}, auth(token));
    onChanged();
    load();
  };

  const saveOrder = async (oid, data) => {
    await axios.patch(`${API}/admin/orders/${oid}`, data, auth(token));
    onChanged();
    load();
  };

  const deleteLead = async () => {
    if (!window.confirm(`Delete lead "${lead.full_name}" including all communications, quotations and orders? This cannot be undone.`)) return;
    await axios.delete(`${API}/admin/leads/${leadId}`, auth(token));
    onChanged();
    onClose();
  };

  if (!detail || !form)
    return (
      <div className="fixed inset-0 z-[60] bg-[#1C120C]/60 backdrop-blur-sm flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-[#D4A359]" />
      </div>
    );

  const lead = detail.lead;

  return (
    <div className="fixed inset-0 z-[60] bg-[#1C120C]/60 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div
        data-testid="lead-detail-panel"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl h-full bg-[#FDFBF7] shadow-2xl overflow-y-auto"
      >
        <div className="sticky top-0 bg-[#1C120C] text-[#FAF6EE] px-6 py-4 flex items-center justify-between z-10">
          <div className="min-w-0">
            <h3 data-testid="lead-detail-name" className="font-serif font-bold text-lg truncate">{lead.full_name}</h3>
            <p className="text-xs text-[#FAF6EE]/60 truncate">
              {lead.business_name} · {lead.country || "—"} · {lead.source || "Website"}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-4">
            <button
              data-testid="lead-detail-delete"
              onClick={deleteLead}
              title="Delete lead"
              className="text-[#FAF6EE]/70 hover:text-red-400 transition-colors"
            >
              <Trash2 size={19} />
            </button>
            <button data-testid="lead-detail-close" onClick={onClose} className="text-[#FAF6EE]/70 hover:text-white transition-colors">
              <X size={22} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-8">
          <section>
            <h4 className="font-serif font-bold text-[#2C1A0C] mb-4">Pipeline & Profile</h4>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Status</label>
                <select data-testid="lead-detail-status" className={`${inputCls} font-semibold`} value={form.status || "new"} onChange={set("status")}>
                  {LEAD_STATUSES.map(([k, l]) => (
                    <option key={k} value={k}>{l}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Lead Source</label>
                <select data-testid="lead-detail-source" className={inputCls} value={form.source || "Website"} onChange={set("source")}>
                  {LEAD_SOURCES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Next Follow-Up</label>
                <input data-testid="lead-detail-followup" type="date" className={inputCls} value={form.follow_up_date} onChange={set("follow_up_date")} />
              </div>
              <div>
                <label className={labelCls}>Full Name</label>
                <input className={inputCls} value={form.full_name} onChange={set("full_name")} />
              </div>
              <div>
                <label className={labelCls}>Company</label>
                <input className={inputCls} value={form.business_name} onChange={set("business_name")} />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input className={inputCls} value={form.business_email} onChange={set("business_email")} />
              </div>
              <div>
                <label className={labelCls}>Phone / WhatsApp</label>
                <input className={inputCls} value={form.phone_whatsapp} onChange={set("phone_whatsapp")} />
              </div>
              <div>
                <label className={labelCls}>Country</label>
                <input className={inputCls} value={form.country} onChange={set("country")} />
              </div>
              <div>
                <label className={labelCls}>Product Interest</label>
                <select className={inputCls} value={form.vanilla_type} onChange={set("vanilla_type")}>
                  {[...new Set([form.vanilla_type, ...PRODUCT_INTERESTS])].filter(Boolean).map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Est. Quantity</label>
                <input className={inputCls} value={form.estimated_quantity} onChange={set("estimated_quantity")} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Notes</label>
                <textarea rows={3} className={inputCls} value={form.notes || ""} onChange={set("notes")} />
              </div>
            </div>
            <button
              data-testid="lead-detail-save"
              onClick={saveProfile}
              disabled={saving}
              className="mt-4 inline-flex items-center gap-2 bg-[#2E4F3E] hover:bg-[#1B3B2B] disabled:opacity-60 text-[#FDFBF7] text-sm font-semibold rounded-full px-5 py-2.5 transition-colors"
            >
              <Save size={14} /> {saving ? "Saving…" : "Save Changes"}
            </button>
          </section>

          <section>
            <h4 className="font-serif font-bold text-[#2C1A0C] mb-4 flex items-center gap-2">
              <MessageSquare size={16} className="text-[#2E4F3E]" /> Communication History
            </h4>
            <form onSubmit={addComm} className="flex gap-2 mb-4">
              <select className={`${inputCls} !w-32`} value={comm.type} onChange={(e) => setComm((c) => ({ ...c, type: e.target.value }))}>
                {COMM_TYPES.map(([k, l]) => (
                  <option key={k} value={k}>{l}</option>
                ))}
              </select>
              <input
                data-testid="comm-input"
                className={inputCls}
                placeholder="e.g. Sent intro WhatsApp, buyer asked for COA…"
                value={comm.content}
                onChange={(e) => setComm((c) => ({ ...c, content: e.target.value }))}
              />
              <button data-testid="comm-add-button" type="submit" className="shrink-0 bg-[#2E4F3E] hover:bg-[#1B3B2B] text-[#FDFBF7] text-sm font-semibold rounded-full px-4 transition-colors">
                Add
              </button>
            </form>
            <div className="space-y-2.5">
              {detail.communications.length === 0 && <p className="text-sm text-[#6E5B4F]">No communication logged yet.</p>}
              {detail.communications.map((c) => (
                <div key={c.id} className="bg-white border border-[#E6DEC3] rounded-2xl px-4 py-3">
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#2E4F3E] bg-[#EAF0EC] rounded-full px-2 py-0.5">{c.type}</span>
                    <span className="text-xs text-[#6E5B4F]">{new Date(c.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-[#2C1A0C]">{c.content}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h4 className="font-serif font-bold text-[#2C1A0C] mb-4 flex items-center gap-2">
              <FileText size={16} className="text-[#2E4F3E]" /> Quotations
            </h4>
            <form data-testid="quote-form" onSubmit={createQuote} className="bg-white border border-[#E6DEC3] rounded-2xl p-4 mb-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="col-span-2 sm:col-span-3">
                  <label className={labelCls}>Product</label>
                  <select className={inputCls} value={quote.product} onChange={setQ("product")}>
                    {PRODUCT_INTERESTS.filter((p) => p !== "Multiple / Custom Mix").map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Quantity (kg)</label>
                  <input data-testid="quote-qty" type="number" step="any" min="0" required className={inputCls} value={quote.quantity} onChange={setQ("quantity")} />
                </div>
                <div>
                  <label className={labelCls}>Unit Price ({quote.currency})</label>
                  <input data-testid="quote-price" type="number" step="any" min="0" required className={inputCls} value={quote.unit_price} onChange={setQ("unit_price")} />
                </div>
                <div>
                  <label className={labelCls}>Shipping Cost</label>
                  <input type="number" step="any" min="0" className={inputCls} value={quote.shipping_cost} onChange={setQ("shipping_cost")} />
                </div>
                <div>
                  <label className={labelCls}>Discount</label>
                  <input type="number" step="any" min="0" className={inputCls} value={quote.discount} onChange={setQ("discount")} />
                </div>
                <div>
                  <label className={labelCls}>Destination</label>
                  <input className={inputCls} value={quote.destination} onChange={setQ("destination")} placeholder="e.g. Netherlands" />
                </div>
                <div>
                  <label className={labelCls}>Payment Terms</label>
                  <input className={inputCls} value={quote.payment_terms} onChange={setQ("payment_terms")} />
                </div>
              </div>
              <div className="flex items-center justify-between mt-4">
                <p data-testid="quote-total" className="font-serif font-bold text-xl text-[#2C1A0C]">
                  Total: {fmtMoney(quoteTotal, quote.currency)}
                </p>
                <button data-testid="quote-create-button" type="submit" disabled={busy} className="bg-[#2E4F3E] hover:bg-[#1B3B2B] disabled:opacity-60 text-[#FDFBF7] text-sm font-semibold rounded-full px-5 py-2.5 transition-colors">
                  {busy ? "Saving…" : "Create Quotation"}
                </button>
              </div>
            </form>
            <div className="space-y-2.5">
              {detail.quotations.map((qt) => {
                const order = detail.orders.find((o) => o.quotation_id === qt.id);
                return (
                  <div key={qt.id} data-testid={`quote-card-${qt.id}`} className="bg-white border border-[#E6DEC3] rounded-2xl px-4 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-[#2C1A0C]">
                        {qt.product} · {qt.quantity} {qt.unit} × {fmtMoney(qt.unit_price, qt.currency)}
                      </p>
                      <p className="font-serif font-bold text-[#2C1A0C]">{fmtMoney(qt.total, qt.currency)}</p>
                    </div>
                    <p className="text-xs text-[#6E5B4F] mt-1">
                      Ship {fmtMoney(qt.shipping_cost, qt.currency)} · Disc {fmtMoney(qt.discount, qt.currency)} · {qt.destination || "—"} · {qt.payment_terms} · {new Date(qt.created_at).toLocaleDateString()}
                    </p>
                    <div className="flex items-center gap-2.5 mt-2.5">
                      <select
                        data-testid={`quote-status-${qt.id}`}
                        value={qt.status}
                        onChange={(e) => setQuoteStatus(qt.id, e.target.value)}
                        className={`${inputCls} !w-36 !py-1.5 text-xs font-semibold`}
                      >
                        {QUOTE_STATUSES.map(([k, l]) => (
                          <option key={k} value={k}>{l}</option>
                        ))}
                      </select>
                      {qt.status === "accepted" && !order && (
                        <button
                          data-testid={`quote-convert-${qt.id}`}
                          onClick={() => convert(qt.id)}
                          className="text-xs font-bold bg-[#D4A359] hover:bg-[#c2924a] text-[#1C120C] rounded-full px-4 py-1.5 transition-colors"
                        >
                          Convert to Order
                        </button>
                      )}
                      {order && <span className="text-xs font-semibold text-[#2E4F3E]">→ Order created</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {detail.orders.length > 0 && (
            <section>
              <h4 className="font-serif font-bold text-[#2C1A0C] mb-4 flex items-center gap-2">
                <Package size={16} className="text-[#2E4F3E]" /> Orders & Shipment
              </h4>
              <div className="space-y-2.5">
                {detail.orders.map((o) => (
                  <OrderCard key={o.id} order={o} onSave={saveOrder} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

const OrderCard = ({ order, onSave }) => {
  const [carrier, setCarrier] = useState(order.carrier || "");
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [eta, setEta] = useState(order.eta || "");
  return (
    <div data-testid={`order-card-${order.id}`} className="bg-white border border-[#E6DEC3] rounded-2xl px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-[#2C1A0C]">{order.product} · {order.quantity} {order.unit}</p>
        <p className="font-serif font-bold text-[#2C1A0C]">{fmtMoney(order.total, order.currency)}</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2.5">
        <select
          data-testid={`order-status-${order.id}`}
          value={order.status}
          onChange={(e) => onSave(order.id, { status: e.target.value })}
          className={`${inputCls} !py-1.5 text-xs font-semibold`}
        >
          {ORDER_STATUSES.map(([k, l]) => (
            <option key={k} value={k}>{l}</option>
          ))}
        </select>
        <input className={`${inputCls} !py-1.5 text-xs`} placeholder="Carrier (e.g. DHL)" value={carrier} onChange={(e) => setCarrier(e.target.value)} />
        <input data-testid={`order-tracking-${order.id}`} className={`${inputCls} !py-1.5 text-xs`} placeholder="Tracking number" value={tracking} onChange={(e) => setTracking(e.target.value)} />
        <input type="date" className={`${inputCls} !py-1.5 text-xs`} value={eta} onChange={(e) => setEta(e.target.value)} />
      </div>
      <button
        data-testid={`order-save-${order.id}`}
        onClick={() => onSave(order.id, { carrier, tracking_number: tracking, eta })}
        className="mt-2.5 text-xs font-semibold text-[#2E4F3E] hover:text-[#1B3B2B] underline underline-offset-2"
      >
        Save shipment info
      </button>
    </div>
  );
};
